/**
 * Call Analytics Page
 * Comprehensive analytics and reporting for call center performance
 * Shows trends, conversion rates, quality scores, and leaderboards
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Phone, CheckCircle, Star, DollarSign, Filter, Download, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { CallRecordingPlayer } from '@/components/agent-console/CallRecordingPlayer';
import { TranscriptViewer } from '@/components/agent-console/TranscriptViewer';
import { CoachingNotes } from '@/components/agent-console/CoachingNotes';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

export default function CallAnalytics() {
  const { theme } = useTheme();
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'quarter'>('week');
  const [volumeTrends, setVolumeTrends] = useState<any[]>([]);
  const [conversionData, setConversionData] = useState<any>(null);
  const [qualityScores, setQualityScores] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [callTypeBreakdown, setCallTypeBreakdown] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Call List State
  const [allCalls, setAllCalls] = useState<any[]>([]);
  const [filteredCalls, setFilteredCalls] = useState<any[]>([]);
  const [expandedCall, setExpandedCall] = useState<string | null>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [callFilters, setCallFilters] = useState({
    agent: 'all',
    callType: 'all',
    company: 'all',
    outcome: 'all'
  });

  useEffect(() => {
    loadAnalyticsData();
    loadAgentsAndCompanies();
    loadAllCalls();
  }, [dateRange]);

  useEffect(() => {
    filterAllCalls();
  }, [allCalls, callFilters]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    await Promise.all([
      loadVolumeTrends(),
      loadConversionData(),
      loadQualityScores(),
      loadLeaderboard(),
      loadCallTypeBreakdown()
    ]);
    setLoading(false);
  };

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (dateRange) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setDate(start.getDate() - 30);
        break;
      case 'quarter':
        start.setDate(start.getDate() - 90);
        break;
    }
    
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const loadVolumeTrends = async () => {
    const { start, end } = getDateRange();

    const { data } = await supabase
      .from('calls')
      .select('id, started_at, answered_at, status')
      .gte('started_at', start)
      .lte('started_at', end)
      .order('started_at', { ascending: true });

    if (data) {
      const byDate: Record<string, { total: number; answered: number; abandoned: number }> = {};
      data.forEach((call: any) => {
        const date = new Date(call.started_at).toISOString().split('T')[0];
        if (!byDate[date]) {
          byDate[date] = { total: 0, answered: 0, abandoned: 0 };
        }
        byDate[date].total += 1;
        if (call.answered_at !== null) {
          byDate[date].answered += 1;
        }
        if (call.status === 'canceled' || call.status === 'no_answer' || call.status === 'abandoned') {
          byDate[date].abandoned += 1;
        }
      });

      const aggregated = Object.entries(byDate)
        .map(([date, v]) => ({ date, ...v }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setVolumeTrends(aggregated);
    } else {
      setVolumeTrends([]);
    }
  };

  const loadConversionData = async () => {
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('calls')
      .select('call_type, disposition')
      .gte('started_at', start)
      .lte('started_at', end)
      .in('call_type', ['sales', 'booking'])
      .in('disposition', ['resolved', 'callback_scheduled', 'abandoned']);
    
    if (data) {
      const conversions = {
        sales: { total: 0, converted: 0 },
        booking: { total: 0, converted: 0 }
      };
      
      data.forEach((call: any) => {
        if (call.call_type === 'sales') {
          conversions.sales.total++;
          if (call.disposition === 'resolved') conversions.sales.converted++;
        } else if (call.call_type === 'booking') {
          conversions.booking.total++;
          if (call.disposition === 'resolved') conversions.booking.converted++;
        }
      });
      
      setConversionData({
        salesRate: conversions.sales.total > 0 
          ? (conversions.sales.converted / conversions.sales.total * 100).toFixed(1)
          : 0,
        bookingRate: conversions.booking.total > 0
          ? (conversions.booking.converted / conversions.booking.total * 100).toFixed(1)
          : 0,
        conversions
      });
    }
  };

  const loadQualityScores = async () => {
    const { start, end } = getDateRange();

    const { data } = await supabase
      .from('calls')
      .select('started_at, quality_score, sentiment_score')
      .gte('started_at', start)
      .lte('started_at', end)
      .order('started_at', { ascending: true });

    if (data) {
      const byDate: Record<string, { qualitySum: number; qualityCount: number; sentimentSum: number; sentimentCount: number }> = {};
      data.forEach((call: any) => {
        const date = new Date(call.started_at).toISOString().split('T')[0];
        if (!byDate[date]) {
          byDate[date] = { qualitySum: 0, qualityCount: 0, sentimentSum: 0, sentimentCount: 0 };
        }
        if (call.quality_score !== null && call.quality_score !== undefined) {
          byDate[date].qualitySum += parseFloat(call.quality_score);
          byDate[date].qualityCount += 1;
        }
        if (call.sentiment_score !== null && call.sentiment_score !== undefined) {
          byDate[date].sentimentSum += parseFloat(call.sentiment_score);
          byDate[date].sentimentCount += 1;
        }
      });

      const scores = Object.entries(byDate)
        .map(([date, v]) => ({
          date,
          quality: v.qualityCount > 0 ? v.qualitySum / v.qualityCount : 0,
          sentiment: v.sentimentCount > 0 ? v.sentimentSum / v.sentimentCount : 0
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setQualityScores(scores);
    } else {
      setQualityScores([]);
    }
  };

  const loadLeaderboard = async () => {
    const { start, end } = getDateRange();

    const { data } = await supabase
      .from('calls')
      .select(`
        agent_id,
        call_center_agents(first_name, last_name),
        quality_score,
        disposition,
        started_at
      `)
      .gte('started_at', start)
      .lte('started_at', end)
      .order('started_at', { ascending: true });

    if (data) {
      const stats: Record<string, any> = {};
      data.forEach((row: any) => {
        if (!row.agent_id) return;
        if (!stats[row.agent_id]) {
          stats[row.agent_id] = {
            agent_id: row.agent_id,
            name: row.call_center_agents
              ? `${row.call_center_agents.first_name} ${row.call_center_agents.last_name}`
              : 'Unknown',
            total_calls: 0,
            resolved_calls: 0,
            quality_sum: 0,
            quality_count: 0
          };
        }
        const s = stats[row.agent_id];
        s.total_calls += 1;
        if (row.disposition === 'resolved') s.resolved_calls += 1;
        if (row.quality_score !== null && row.quality_score !== undefined) {
          s.quality_sum += parseFloat(row.quality_score);
          s.quality_count += 1;
        }
      });

      const leaderboardArr = Object.values(stats)
        .map((agent: any) => ({
          ...agent,
          avg_quality: agent.quality_count > 0
            ? (agent.quality_sum / agent.quality_count).toFixed(2)
            : '0.00',
          resolution_rate: agent.total_calls > 0
            ? ((agent.resolved_calls / agent.total_calls) * 100).toFixed(1)
            : '0.0'
        }))
        .sort((a: any, b: any) => parseFloat(b.avg_quality) - parseFloat(a.avg_quality))
        .slice(0, 10);

      setLeaderboard(leaderboardArr as any[]);
    } else {
      setLeaderboard([]);
    }
  };

  const loadCallTypeBreakdown = async () => {
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('calls')
      .select('call_type')
      .gte('started_at', start)
      .lte('started_at', end)
      .not('call_type', 'is', null);
    
    if (data) {
      const breakdown = data.reduce((acc: any, call: any) => {
        const type = call.call_type || 'general';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      
      const chartData = Object.entries(breakdown).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));
      
      setCallTypeBreakdown(chartData);
    }
  };

  const loadAgentsAndCompanies = async () => {
    const { data: agentsData } = await supabase
      .from('call_center_agents')
      .select('id, first_name, last_name')
      .order('first_name');
    
    const { data: companiesData } = await supabase
      .from('twilio_phone_numbers')
      .select('company_name, phone_number')
      .eq('is_active', true);
    
    setAgents(agentsData || []);
    setCompanies(companiesData || []);
  };

  const loadAllCalls = async () => {
    const { start, end } = getDateRange();
    
    const { data } = await supabase
      .from('calls')
      .select(`
        *,
        call_center_agents(first_name, last_name),
        call_outcomes(*),
        call_recordings(*),
        call_transcripts(*),
        call_coaching_notes(*)
      `)
      .gte('started_at', start)
      .lte('started_at', end)
      .order('started_at', { ascending: false })
      .limit(200);
    
    setAllCalls(data || []);
  };

  const filterAllCalls = () => {
    let filtered = [...allCalls];

    if (callFilters.agent !== 'all') {
      filtered = filtered.filter(c => c.agent_id === callFilters.agent);
    }

    if (callFilters.callType !== 'all') {
      filtered = filtered.filter(c => c.direction === callFilters.callType);
    }

    if (callFilters.company !== 'all') {
      filtered = filtered.filter(c => {
        const company = companies.find(co => 
          co.phone_number === c.from_number || co.phone_number === c.to_number
        );
        return company?.company_name === callFilters.company;
      });
    }

    if (callFilters.outcome !== 'all') {
      filtered = filtered.filter(c => 
        c.call_outcomes?.[0]?.outcome_type === callFilters.outcome
      );
    }

    setFilteredCalls(filtered);
  };

  const getOutcomeColors = (outcomeType?: string) => {
    switch (outcomeType) {
      case 'opportunity_booked':
        return { border: 'border-green-500', bg: 'bg-green-50', badge: 'bg-green-100 text-green-800', icon: '🟢' };
      case 'opportunity_missed':
        return { border: 'border-red-500', bg: 'bg-red-50', badge: 'bg-red-100 text-red-800', icon: '🔴' };
      case 'neutral':
        return { border: 'border-yellow-500', bg: 'bg-yellow-50', badge: 'bg-yellow-100 text-yellow-800', icon: '🟡' };
      default:
        return { border: 'border-gray-300', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-800', icon: '⚪️' };
    }
  };

  const exportCallsToCSV = () => {
    const csv = [
      ['Date', 'Time', 'Agent', 'Direction', 'Number', 'Duration', 'Outcome', 'Company'].join(','),
      ...filteredCalls.map(call => [
        new Date(call.started_at).toLocaleDateString(),
        new Date(call.started_at).toLocaleTimeString(),
        call.call_center_agents ? `${call.call_center_agents.first_name} ${call.call_center_agents.last_name}` : 'N/A',
        call.direction,
        call.direction === 'inbound' ? call.from_number : call.to_number,
        call.duration_seconds || 0,
        call.call_outcomes?.[0]?.outcome_type || 'N/A',
        companies.find(c => c.phone_number === call.from_number || c.phone_number === call.to_number)?.company_name || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-4xl mb-4">📊</div>
          <div className="text-gray-600">Loading analytics...</div>
        </div>
      </div>
    );
  }

  const totalCalls = volumeTrends.reduce((sum, day) => sum + day.total, 0);
  const answeredCalls = volumeTrends.reduce((sum, day) => sum + day.answered, 0);
  const answerRate = totalCalls > 0 ? ((answeredCalls / totalCalls) * 100).toFixed(1) : '0.0';
  const avgQuality = qualityScores.length > 0
    ? (qualityScores.reduce((sum, s) => sum + s.quality, 0) / qualityScores.length).toFixed(2)
    : '0.00';

  return (
    <AdminPageLayout
      title="Call Analytics"
      subtitle="Performance metrics and trends"
      actions={
        <>
          {(['today', 'week', 'month', 'quarter'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className="px-4 py-2 rounded-lg font-medium transition-colors"
              style={{
                backgroundColor: dateRange === range ? theme.colors.primaryColor : theme.colors.bgCard,
                color: dateRange === range ? '#ffffff' : theme.colors.textPrimary,
                border: `1px solid ${dateRange === range ? theme.colors.primaryColor : theme.colors.borderColor}`
              }}
            >
              {range === 'today' ? 'Today' : range === 'week' ? '7 Days' : range === 'month' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </>
      }
    >
      {/* Key Metrics */}
      <StatsGrid cols={4}>
        <StatCard
          label="Total Calls"
          value={totalCalls}
          icon={Phone}
          subtitle="↑ 12%"
        />
        <StatCard
          label="Answer Rate"
          value={`${answerRate}%`}
          icon={CheckCircle}
          variant="success"
          subtitle="↑ 3%"
        />
        <StatCard
          label="Avg Quality Score"
          value={avgQuality}
          icon={Star}
          subtitle="↑ 0.2"
        />
        <StatCard
          label="Sales Conversion"
          value={`${conversionData?.salesRate || 0}%`}
          icon={DollarSign}
          subtitle="↓ 1.5%"
        />
      </StatsGrid>

      {/* Call Volume Trends */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Call Volume Trends</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={volumeTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="total" stroke="#3B82F6" name="Total Calls" strokeWidth={2} />
            <Line type="monotone" dataKey="answered" stroke="#10B981" name="Answered" strokeWidth={2} />
            <Line type="monotone" dataKey="abandoned" stroke="#EF4444" name="Abandoned" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Quality Score Trends */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Quality & Sentiment Trends</h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={qualityScores}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="quality" stroke="#8B5CF6" name="Quality Score" strokeWidth={2} />
              <Line type="monotone" dataKey="sentiment" stroke="#F59E0B" name="Sentiment" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Call Type Breakdown */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Call Type Distribution</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={callTypeBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {callTypeBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Conversion Rates */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Conversion Rates</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Sales Conversion</span>
              <span className="text-2xl font-bold" style={{ color: theme.colors.primaryColor }}>{conversionData?.salesRate || 0}%</span>
            </div>
            <div className="w-full rounded-full h-3" style={{ backgroundColor: theme.colors.bgHover }}>
              <div 
                className="h-3 rounded-full transition-all"
                style={{ 
                  width: `${conversionData?.salesRate || 0}%`,
                  backgroundColor: theme.colors.primaryColor
                }}
              />
            </div>
            <p className="text-xs mt-1" style={{ color: theme.colors.textTertiary }}>
              {conversionData?.conversions.sales.converted || 0} of {conversionData?.conversions.sales.total || 0} calls converted
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Booking Conversion</span>
              <span className="text-2xl font-bold" style={{ color: theme.colors.successText }}>{conversionData?.bookingRate || 0}%</span>
            </div>
            <div className="w-full rounded-full h-3" style={{ backgroundColor: theme.colors.bgHover }}>
              <div 
                className="h-3 rounded-full transition-all"
                style={{ 
                  width: `${conversionData?.bookingRate || 0}%`,
                  backgroundColor: theme.colors.successBorder
                }}
              />
            </div>
            <p className="text-xs mt-1" style={{ color: theme.colors.textTertiary }}>
              {conversionData?.conversions.booking.converted || 0} of {conversionData?.conversions.booking.total || 0} calls converted
            </p>
          </div>
        </div>
      </Card>

      {/* Performance Leaderboard */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Performance Leaderboard</h2>
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead style={{ backgroundColor: theme.colors.bgHover }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textTertiary }}>Rank</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textTertiary }}>Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textTertiary }}>Total Calls</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textTertiary }}>Resolved</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textTertiary }}>Resolution Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: theme.colors.textTertiary }}>Avg Quality</th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: theme.colors.bgCard }}>
              {leaderboard.map((agent, index) => (
                <tr 
                  key={agent.agent_id}
                  style={{ 
                    backgroundColor: index < 3 ? theme.colors.warningBg : 'transparent',
                    borderBottom: `1px solid ${theme.colors.borderColor}`
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-300 text-orange-900' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium" style={{ color: theme.colors.textPrimary }}>{agent.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                    {agent.total_calls}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: theme.colors.textPrimary }}>
                    {agent.resolved_calls}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                      style={{
                        backgroundColor: theme.colors.successBg,
                        color: theme.colors.successText
                      }}
                    >
                      {agent.resolution_rate}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium mr-2" style={{ color: theme.colors.textPrimary }}>{agent.avg_quality}</span>
                      <span className="text-yellow-400">⭐</span>
                    </div>
                  </td>
                </tr>
              ))}
              {leaderboard.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center" style={{ color: theme.colors.textTertiary }}>
                    No agent data available for this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detailed Call History with AI Analysis */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>Detailed Call History</h2>
            <p className="text-sm mt-1" style={{ color: theme.colors.textSecondary }}>
              {filteredCalls.length} of {allCalls.length} calls
            </p>
          </div>
          <button
            onClick={exportCallsToCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: theme.colors.primaryColor,
              color: '#ffffff'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div 
          className="grid grid-cols-4 gap-4 mb-6 p-4 rounded-lg"
          style={{ backgroundColor: theme.colors.bgHover }}
        >
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              <Filter className="w-4 h-4 inline mr-1" />
              Agent
            </label>
            <select
              value={callFilters.agent}
              onChange={(e) => setCallFilters({ ...callFilters, agent: e.target.value })}
              className="w-full px-3 py-2 rounded-lg focus:ring-2"
              style={{
                backgroundColor: theme.colors.bgCard,
                border: `1px solid ${theme.colors.borderColor}`,
                color: theme.colors.textPrimary
              }}
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.first_name} {agent.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>Call Type</label>
            <select
              value={callFilters.callType}
              onChange={(e) => setCallFilters({ ...callFilters, callType: e.target.value })}
              className="w-full px-3 py-2 rounded-lg focus:ring-2"
              style={{
                backgroundColor: theme.colors.bgCard,
                border: `1px solid ${theme.colors.borderColor}`,
                color: theme.colors.textPrimary
              }}
            >
              <option value="all">All Types</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>Company</label>
            <select
              value={callFilters.company}
              onChange={(e) => setCallFilters({ ...callFilters, company: e.target.value })}
              className="w-full px-3 py-2 rounded-lg focus:ring-2"
              style={{
                backgroundColor: theme.colors.bgCard,
                border: `1px solid ${theme.colors.borderColor}`,
                color: theme.colors.textPrimary
              }}
            >
              <option value="all">All Companies</option>
              {companies.map(company => (
                <option key={company.phone_number} value={company.company_name}>
                  {company.company_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>Outcome</label>
            <select
              value={callFilters.outcome}
              onChange={(e) => setCallFilters({ ...callFilters, outcome: e.target.value })}
              className="w-full px-3 py-2 rounded-lg focus:ring-2"
              style={{
                backgroundColor: theme.colors.bgCard,
                border: `1px solid ${theme.colors.borderColor}`,
                color: theme.colors.textPrimary
              }}
            >
              <option value="all">All Outcomes</option>
              <option value="opportunity_booked">🟢 Opportunity Booked</option>
              <option value="opportunity_missed">🔴 Opportunity Missed</option>
              <option value="neutral">🟡 No Opportunity</option>
              <option value="incomplete">⚪️ Incomplete</option>
            </select>
          </div>
        </div>

        {/* Call List */}
        <div className="space-y-3 max-h-[800px] overflow-y-auto">
          {filteredCalls.map((call) => {
            const outcome = call.call_outcomes?.[0];
            const colors = getOutcomeColors(outcome?.outcome_type);
            const isExpanded = expandedCall === call.call_sid;
            const agentName = call.call_center_agents 
              ? `${call.call_center_agents.first_name} ${call.call_center_agents.last_name}`
              : 'Unknown';

            return (
              <div
                key={call.id}
                className={`border-2 rounded-lg overflow-hidden ${colors.border} ${colors.bg}`}
              >
                <div
                  className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setExpandedCall(isExpanded ? null : call.call_sid)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="text-2xl">{colors.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-900">
                            {call.direction === 'inbound' ? call.from_number : call.to_number}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}>
                            {outcome?.outcome_type?.replace('_', ' ') || 'Processing'}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{agentName}</span>
                          <span className="flex items-center gap-1">
                            <Phone className={`w-3 h-3 ${call.direction === 'inbound' ? 'rotate-135' : 'rotate-45'}`} />
                            {call.direction}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(call.started_at)}
                          </span>
                          {call.duration_seconds && (
                            <span>{formatDuration(call.duration_seconds)}</span>
                          )}
                        </div>

                        {outcome?.ai_analysis_summary && (
                          <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                            {outcome.ai_analysis_summary}
                          </p>
                        )}
                      </div>
                    </div>

                    <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-300 p-4 space-y-4 bg-white">
                    {call.call_recordings?.[0] && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 mb-2">Recording</h4>
                        <CallRecordingPlayer
                          recordingUrl={call.call_recordings[0].recording_url}
                          duration={call.call_recordings[0].recording_duration_seconds || call.duration_seconds || 0}
                          callSid={call.call_sid}
                        />
                      </div>
                    )}

                    {call.call_transcripts?.[0] && (
                      <TranscriptViewer
                        fullTranscript={call.call_transcripts[0].full_transcript}
                        agentTranscript={call.call_transcripts[0].agent_transcript}
                        customerTranscript={call.call_transcripts[0].customer_transcript}
                      />
                    )}

                    {call.call_coaching_notes && call.call_coaching_notes.length > 0 && (
                      <CoachingNotes notes={call.call_coaching_notes} />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filteredCalls.length === 0 && (
            <div className="text-center py-12" style={{ color: theme.colors.textTertiary }}>
              <Phone className="w-12 h-12 mx-auto mb-3" style={{ color: theme.colors.borderColor }} />
              <p>No calls found</p>
              <p className="text-xs mt-2">Try adjusting your filters</p>
            </div>
          )}
        </div>
      </Card>
    </AdminPageLayout>
  );
}
